import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Send, X, Loader2, Mail } from "lucide-react";

interface Recipient {
  email: string;
  name?: string;
}

interface AdminEmailComposerProps {
  open: boolean;
  onClose: () => void;
  recipients: Recipient[];
}

const EMAIL_TEMPLATES = [
  {
    id: "custom",
    name: "Custom Message",
    subject: "",
    body: "",
  },
  {
    id: "welcome",
    name: "Welcome Message",
    subject: "Welcome to TailorWiz!",
    body: `Hi {firstName},

Welcome to TailorWiz! We're excited to have you on board.

Here are a few things you can do to get started:
• Tailor your resume to any job description
• Generate professional cover letters
• Optimize your LinkedIn profile

If you have any questions, just reply to this email and we'll be happy to help.

Best,
The TailorWiz Team`,
  },
  {
    id: "credits",
    name: "Credits Added",
    subject: "Credits Added to Your Account",
    body: `Hi {firstName},

Great news! We've added credits to your TailorWiz account.

You can use these credits to:
• Tailor resumes (5 credits each)
• Generate cover letters (2 credits each)
• Optimize your LinkedIn profile (5 credits)

Log in now to start using your credits!

Best,
The TailorWiz Team`,
  },
  {
    id: "feature",
    name: "New Feature Announcement",
    subject: "New Feature: Check It Out!",
    body: `Hi {firstName},

We've been working hard on something exciting, and it's finally here!

[Describe the new feature here]

Log in to try it out today.

Best,
The TailorWiz Team`,
  },
  {
    id: "reminder",
    name: "Activity Reminder",
    subject: "We Miss You at TailorWiz!",
    body: `Hi {firstName},

It's been a while since we've seen you on TailorWiz!

Your credits are waiting, and we've added some great new features since your last visit. Come back and see what's new!

Best,
The TailorWiz Team`,
  },
];

export function AdminEmailComposer({ open, onClose, recipients }: AdminEmailComposerProps) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("custom");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isHtml, setIsHtml] = useState(false);
  const [senderName, setSenderName] = useState("TailorWiz");

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = EMAIL_TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setMessage(template.body);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter both a subject and message.",
        variant: "destructive",
      });
      return;
    }

    setSending(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-admin-email", {
        body: {
          recipients,
          subject,
          message,
          isHtml,
          senderName,
        },
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Emails Sent!",
          description: `Successfully sent ${data.sent} email(s)${data.failed > 0 ? `. ${data.failed} failed.` : "."}`,
        });
        onClose();
        // Reset form
        setSelectedTemplate("custom");
        setSubject("");
        setMessage("");
      } else {
        throw new Error(data.error || "Failed to send emails");
      }
    } catch (error: any) {
      console.error("Error sending emails:", error);
      toast({
        title: "Send Failed",
        description: error.message || "Failed to send emails. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Compose Email
          </DialogTitle>
          <DialogDescription>
            Send an email to {recipients.length} recipient{recipients.length !== 1 ? "s" : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Recipients Preview */}
          <div>
            <Label className="text-sm font-medium">Recipients</Label>
            <div className="mt-1 flex flex-wrap gap-1 max-h-24 overflow-y-auto p-2 bg-muted rounded-md">
              {recipients.slice(0, 10).map((r, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs">
                  {r.name || r.email}
                </Badge>
              ))}
              {recipients.length > 10 && (
                <Badge variant="outline" className="text-xs">
                  +{recipients.length - 10} more
                </Badge>
              )}
            </div>
          </div>

          {/* Template Selection */}
          <div>
            <Label htmlFor="template">Email Template</Label>
            <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Choose a template..." />
              </SelectTrigger>
              <SelectContent>
                {EMAIL_TEMPLATES.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sender Name */}
          <div>
            <Label htmlFor="senderName">Sender Name</Label>
            <Input
              id="senderName"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="TailorWiz"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Emails will appear from "{senderName} &lt;onboarding@resend.dev&gt;"
            </p>
          </div>

          {/* Subject */}
          <div>
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject..."
              className="mt-1"
            />
          </div>

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="message">Message</Label>
              <div className="flex items-center gap-2">
                <Label htmlFor="html-mode" className="text-xs text-muted-foreground">
                  HTML Mode
                </Label>
                <Switch
                  id="html-mode"
                  checked={isHtml}
                  onCheckedChange={setIsHtml}
                />
              </div>
            </div>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Enter your message..."
              className="mt-1 min-h-[200px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use {"{name}"} or {"{firstName}"} to personalize. Replies go to marcus@tailorwiz.com.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending || !subject || !message}>
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send to {recipients.length} Recipient{recipients.length !== 1 ? "s" : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
